import React from 'react';
import { useEffect, useRef, useState } from 'react';
import { type ReactNode } from 'react';
import { Portal } from 'react-portal';

export const DRAGONFRUIT_PORTAL_ROOT_ID = 'dragonfruit-portal';

/**
 * Wrapper around react-portal's Portal that renders children into the correct
 * DOM node. In fullscreen mode, content renders into the fullscreen element;
 * otherwise it renders into the default #dragonfruit-portal root.
 *
 * If the portal's own content contains the fullscreen element (e.g. a Modal
 * wrapping a fullscreen-able view), it stays on the default root to avoid
 * moving a parent into its own descendant.
 */
export function DragonfruitPortal({ children }: { children: ReactNode }): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(!!document.fullscreenElement);

  useEffect(function syncFullscreenState() {
    const handler = (): void => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return (): void => document.removeEventListener('fullscreenchange', handler);
  }, []);

  // Skip re-routing when this portal's content is an ancestor of the fullscreen
  // element (e.g. a Modal whose children contain the fullscreen-able view).
  // Re-routing would move the parent into its own descendant, destroying the
  // DOM subtree and causing the portaled content to vanish.
  const containsFullscreenElement =
    isFullscreen && containerRef.current?.contains(document.fullscreenElement) === true;

  const target =
    isFullscreen && !containsFullscreenElement
      ? document.fullscreenElement
      : document.getElementById(DRAGONFRUIT_PORTAL_ROOT_ID);

  return (
    <Portal node={target}>
      {/* display:contents keeps this wrapper invisible to CSS layout so it
          doesn't introduce an extra box that could break flex/grid sizing or
          direct-child selectors in consumers (modals, tooltips, menus, etc.). */}
      <div ref={containerRef} style={{ display: 'contents' }}>
        {children}
      </div>
    </Portal>
  );
}
